<template>
    <div class="tutor-theme grid flex flex-column justify-content-center align-items-center w-full ">
        <CustomBreadcrumb class="flex align-self-start p-4 "
            :items="[{ label: 'Profile Settings' }, { label: 'Profile' }]" />
        <span class="H6-Medium block text-center mt-6">Add details about yourself</span>
        <form class=" md:col-11 md:p-5 mt-4 pl-4">
            <div class=" w-full col-12 grid ">

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2 relative">
                        <label for="firstName" class="Lable">First Name</label>
                        <InputText id="firstName" v-model="form.firstName" class="w-full" variant="filled" size="small"
                            @blur="markTouched('firstName')" />
                        <small v-if="errors.firstName" class="text-red-500 text-xs absolute" style="bottom: -22px; left: 2px;">{{ errors.firstName }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2 relative">
                        <label for="lastName" class="Lable">Last Name</label>
                        <InputText id="lastName" v-model="form.lastName" class="w-full" variant="filled" size="small"
                            @blur="markTouched('lastName')" />
                        <small v-if="errors.lastName" class="text-red-500 text-xs absolute" style="bottom: -22px; left: 2px;">{{ errors.lastName }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2 relative">
                        <label for="nic" class="Lable">NIC</label>
                        <InputText id="nic" v-model="form.nic" class="w-full" variant="filled" size="small"
                            maxlength="12" @keypress="allowNicChars" @blur="markTouched('nic')" />
                        <small v-if="errors.nic" class="text-red-500 text-xs absolute" style="bottom: -22px; left: 2px;">{{ errors.nic }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2 relative">
                        <label for="mobileNumber" class="Lable">Phone Number</label>
                        <div class="flex gap-2 w-full">
                            <Select v-model="form.phoneCountryCode" :options="countryCodeOptions" optionLabel="name"
                                optionValue="code" placeholder="Code" class="phone-country-select" />
                            <InputText id="mobileNumber" v-model="form.phoneNumber" type="tel"
                                class="w-full" variant="filled" size="small"
                                maxlength="15" @keypress="onlyAllowNumbers" @blur="markTouched('phoneNumber')" />
                        </div>
                        <small v-if="errors.phoneNumber" class="text-red-500 text-xs absolute" style="bottom: -22px; left: 2px;">{{ errors.phoneNumber }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2 relative">
                        <label for="address" class="Lable">Address</label>
                        <InputText id="address" v-model="form.address" class="w-full" variant="filled" size="small"
                            @blur="markTouched('address')" />
                        <small v-if="errors.address" class="text-red-500 text-xs absolute" style="bottom: -22px; left: 2px;">{{ errors.address }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2 relative">
                        <label for="city" class="Lable">City</label>
                        <Select v-model="form.city" appendTo="self" :options="cities" size="small" variant="filled" optionLabel="name"
                            placeholder="Select your city" class="w-full" @change="markTouched('city')" />
                        <small v-if="errors.city" class="text-red-500 text-xs absolute" style="bottom: -22px; left: 2px;">{{ errors.city }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-4 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2 relative">
                        <label for="dob" class="Lable">Date of Birth</label>
                        <DatePicker v-model="form.dob" appendTo="self" showIcon fluid size="small" variant="filled"
                            iconDisplay="input" @date-select="markTouched('dob')" />
                        <small v-if="errors.dob" class="text-red-500 text-xs absolute" style="bottom: -22px; left: 2px;">{{ errors.dob }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-4 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2 relative">
                        <label for="gender" class="Lable">Gender</label>
                        <Select v-model="form.gender" appendTo="self" :options="gender" size="small" variant="filled"
                            optionValue="value" optionLabel="label" placeholder="Select your gender" class="w-full"
                            @change="markTouched('gender')" />
                        <small v-if="errors.gender" class="text-red-500 text-xs absolute" style="bottom: -22px; left: 2px;">{{ errors.gender }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-4 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2 relative">
                        <label for="nationality" class="Lable">Nationality</label>
                        <NationalitySelect v-model="form.nationality" label="" placeholder="Select your nationality" />
                    </div>
                </div>

                <div class="col-12">
                    <div class="flex flex-column gap-2 pt-2">
                        <label for="bio" class="block">Bio</label>
                        <Textarea id="bio" v-model="form.bio" rows="4" class="w-full" variant="filled" size="small" />
                    </div>
                </div>

                <div class="flex flex-column pt-4 px-2">
                    <Button label="Save" class="teal-button w-full md:w-auto" :disabled="!isFormDirty"
                        @click.prevent="handleSubmit" size="small" />
                </div>
            </div>
        </form>
    </div>
</template>

<script setup>
import { ref, computed, defineProps, watch, defineEmits } from "vue";
import CustomBreadcrumb from '@/components/CustomBreadcrumb.vue';
import NationalitySelect from '@/components/common/NationalitySelect.vue';
import { Button, InputText, Select, Textarea } from "primevue";
import DatePicker from 'primevue/datepicker';
import { gender } from "@/utils/options";
import { cities } from "@/utils/cities";
import { countryCodeOptions } from "@/utils/countryCodeOptions";

const emit = defineEmits(["update-profile"]);
const props = defineProps({ profile: { type: Object, required: true } });

const form = ref({
    firstName: "", lastName: "", nic: "", mobileNumber: "",
    phoneCountryCode: "+94", phoneNumber: "",
    nationality: "Sri Lanka",
    address: "", city: null, dob: null, gender: null, bio: "",
});

const originalForm = ref(null);
const touchedFields = ref(new Set()); // Tracks which fields the user interacted with

const parsePhoneFromProfile = (mobileNumber) => {
    if (!mobileNumber) return { phoneCountryCode: "+94", phoneNumber: "" };
    const numStr = String(mobileNumber);
    if (numStr.startsWith("+")) {
        const sortedCodes = [...countryCodeOptions.map(c => c.code)].sort((a, b) => b.length - a.length);
        for (const code of sortedCodes) {
            if (numStr.startsWith(code)) {
                return { phoneCountryCode: code, phoneNumber: numStr.slice(code.length) };
            }
        }
    }
    return { phoneCountryCode: "+94", phoneNumber: numStr };
};

watch(() => props.profile, (newProfile) => {
    if (newProfile) {
        const phone = parsePhoneFromProfile(newProfile.mobileNumber);
        form.value = {
            firstName: newProfile.firstName || "",
            lastName: newProfile.lastName || "",
            nic: newProfile.NIC || "",
            mobileNumber: newProfile.mobileNumber || "",
            phoneCountryCode: phone.phoneCountryCode,
            phoneNumber: phone.phoneNumber,
            address: newProfile.address || "",
            city: validateCity(newProfile.city) || null,
            dob: newProfile.dateOfBirth ? new Date(newProfile.dateOfBirth) : null,
            gender: newProfile.gender || null,
            nationality: newProfile.nationality || "Sri Lanka",
            bio: newProfile.tutorBio || "",
        };
        originalForm.value = JSON.parse(JSON.stringify(form.value));
        if (originalForm.value.dob) originalForm.value.dob = new Date(originalForm.value.dob);
    }
}, { immediate: true });

const markTouched = (field) => touchedFields.value.add(field);

const errors = computed(() => {
    const errs = {};
    const rules = {
        firstName: "First name is required",
        lastName: "Last name is required",
        nic: "NIC is required",
        phoneNumber: "Phone number is required",
        address: "Address is required",
        city: "City is required",
        dob: "Date of birth is required",
        gender: "Gender is required",
    };

    Object.keys(rules).forEach(field => {
        const val = form.value[field];
        const isFieldEmpty = !val || (typeof val === 'string' && !val.trim());
        
        // Show required error only if touched or modified
        if (touchedFields.value.has(field) && isFieldEmpty) {
            errs[field] = rules[field];
        }

        // Specific Format Validations (Shown if not empty)
        if (!isFieldEmpty) {
            if (field === 'nic') {
                const isValid = /^[0-9]{9}[vVxX]$/.test(val) || /^[0-9]{12}$/.test(val);
                if (!isValid) errs.nic = "Invalid NIC format";
            }
            if (field === 'phoneNumber') {
                if (val.length < 6 || val.length > 15) errs.phoneNumber = "Invalid phone number";
            }
        }
    });

    return errs;
});

const isFormDirty = computed(() => {
    if (!originalForm.value) return false;
    return JSON.stringify(form.value) !== JSON.stringify(originalForm.value);
});

const onlyAllowNumbers = (event) => {
    if (!/[0-9]/.test(String.fromCharCode(event.which))) event.preventDefault();
};

const allowNicChars = (event) => {
    if (!/[0-9vVxX]/.test(String.fromCharCode(event.which))) event.preventDefault();
};

const sanitizePhoneNumber = () => {
    form.value.phoneNumber = form.value.phoneNumber.replace(/\D/g, "");
    form.value.mobileNumber = `${form.value.phoneCountryCode}${form.value.phoneNumber}`;
};

function validateCity(code) {
    return cities.find(g => g.code === code) || null;
}

function handleSubmit() {
    // Mark all required fields as touched to trigger error display on Save
    Object.keys(form.value).forEach(key => { if(key !== 'bio') touchedFields.value.add(key) });
    sanitizePhoneNumber();

    if (Object.keys(errors.value).length === 0) {
        const payload = {
            ...form.value,
            NIC: form.value.nic,
            city: form.value.city?.code || null,
            dateOfBirth: form.value.dob ? form.value.dob.toISOString() : null,
            nationality: form.value.nationality || null,
            tutorBio: form.value.bio,
        };
        emit("update-profile", payload);
    }
}
</script>

<style scoped>
/* .wrapper {
    height: 95%;
    margin-bottom: 10px;
    background-color: white;
    border-radius: 10px;
    box-shadow: rgba(60, 64, 67, 0.3) 0px 1px 2px 0px,
        rgba(60, 64, 67, 0.15) 0px 1px 3px 1px;
} */

.phone-country-select {
    min-width: 140px;
    max-width: 180px;
}
</style>
